import React, { useState, useEffect } from "react";
import { Form, Input, Button, Typography } from "antd";
import { SafetyOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "../../store/context/theme-context";
import { useTenant } from "../../store/context/tenant-context";
import { useNotification } from "../../store/context/notification-context";
import { useApiRequests } from "../../utils/api/https/executor";
import LanguageSelector from "../../shared/languages/LanguageSelector";
import TenantSwitcher from "../../components/General/TenantSwitcher";

const { Title, Text, Link } = Typography;

const VerificationPage = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const tenant = useTenant();
  const { theme } = useTheme();
  const { triggerNotification } = useNotification();
  const { verifyOtp, resendOtp } = useApiRequests();

  const isDark = theme === "dark";
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  
  // Get verification context from navigation state
  const { state } = location;
  const { requestId, userId, emailAddress, phoneNumber, verificationType = "email" } = state || {};

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  const handleVerify = async (values) => {
    try {
      setIsLoading(true);
      const otpCode = Object.values(values).join("");

      const response = await verifyOtp({
        requestId,
        userId,
        otp: otpCode,
      });

      if (response?.ok) {
        triggerNotification({
          type: "success",
          message: "Verification Successful",
          description: "Your account has been verified successfully!",
        });
        
        // Navigate based on context
        if (state?.redirectTo) {
          navigate(state.redirectTo);
        } else {
          navigate("/authentication/login");
        }
      } else {
        triggerNotification({
          type: "error",
          message: "Verification Failed",
          description: response?.outData?.message || "Invalid verification code. Please try again.",
        });
      }
    } catch (error) {
      triggerNotification({
        type: "error",
        message: "Error",
        description: "An error occurred during verification. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setIsResending(true);
      const response = await resendOtp({ requestId, userId });

      if (response?.ok) {
        triggerNotification({
          type: "success",
          message: "Code Sent",
          description: "A new verification code has been sent.",
        });
        setCountdown(60);
        setCanResend(false);
      } else {
        triggerNotification({
          type: "error",
          message: "Resend Failed",
          description: response?.outData?.message || "Failed to resend code. Please try again.",
        });
      }
    } catch (error) {
      triggerNotification({
        type: "error",
        message: "Error",
        description: "An error occurred while resending the code.",
      });
    } finally {
      setIsResending(false);
    }
  };

  // Auto-focus and auto-tab for OTP inputs
  const handleOtpKeyUp = (index, e) => {
    if (e.key !== "Backspace" && e.target.value) {
      const nextInput = document.querySelector(`input[name="otp${index + 1}"]`);
      if (nextInput) {
        nextInput.focus();
      }
    } else if (e.key === "Backspace" && !e.target.value && index > 0) {
      const prevInput = document.querySelector(`input[name="otp${index - 1}"]`);
      if (prevInput) {
        prevInput.focus();
      }
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: isDark
          ? `linear-gradient(135deg, #1a1a2e 0%, ${tenant.theme.colorPrimary}40 50%, #16213e 100%)`
          : `linear-gradient(135deg, ${tenant.theme.colorPrimary}85 0%, ${tenant.theme.colorPrimary}95 100%)`,
        position: "relative",
        overflow: "hidden",
        padding: "20px",
      }}
    >
      {/* Decorative Elements */}
      <div
        style={{
          position: "absolute",
          top: "-10%",
          right: "-5%",
          width: "500px",
          height: "500px",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${tenant.theme.colorPrimary}10 0%, transparent 70%)`,
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "-10%",
          left: "-5%",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${tenant.theme.colorPrimary}15 0%, transparent 70%)`,
          pointerEvents: "none",
        }}
      />

      {/* Top Bar */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          padding: "16px 24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          zIndex: 10,
        }}
      >
        {/* Development Tenant Switcher - Only show on localhost */}
        {process.env.NODE_ENV === 'development' && 
         (window.location.hostname === 'localhost' || 
          window.location.hostname === '127.0.0.1') && (
          <TenantSwitcher />
        )}
        <LanguageSelector />
      </div>

      {/* Main Card */}
      <div
        style={{
          width: "100%",
          maxWidth: "480px",
          backgroundColor: isDark ? "rgba(31, 31, 31, 0.95)" : "rgba(255, 255, 255, 0.98)",
          borderRadius: "20px",
          padding: "56px 48px",
          boxShadow: isDark
            ? "0 30px 80px rgba(0, 0, 0, 0.5)"
            : "0 30px 80px rgba(0, 0, 0, 0.2)",
          backdropFilter: "blur(10px)",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Back Button */}
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          style={{
            position: "absolute",
            top: "20px",
            left: "20px",
            color: isDark ? "#ffffff" : "#000000",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          Back
        </Button>

        {/* Logo and Title */}
        <div style={{ textAlign: "center", marginBottom: "40px" }}>
          <img
            src={isDark ? tenant.darkLogo : tenant.logo}
            alt={tenant.name}
            style={{
              maxWidth: "100px",
              height: "auto",
              marginBottom: "20px",
              display: "block",
              marginLeft: "auto",
              marginRight: "auto",
              filter: isDark ? "brightness(1.1)" : "none",
            }}
          />
          
          <div
            style={{
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              backgroundColor: `${tenant.theme.colorPrimary}15`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
            }}
          >
            <SafetyOutlined style={{ fontSize: "32px", color: tenant.theme.colorPrimary }} />
          </div>

          <Title
            level={2}
            style={{
              margin: "0 0 8px 0",
              color: isDark ? "#ffffff" : "#000000",
              fontSize: "28px",
              fontWeight: 600,
            }}
          >
            Verify Your Account
          </Title>
          <Text
            style={{
              color: isDark ? "#a0a0a0" : "#595959",
              fontSize: "15px",
              display: "block",
              lineHeight: "1.6",
            }}
          >
            We've sent a 6-digit verification code to{" "}
            <strong style={{ color: tenant.theme.colorPrimary }}>
              {verificationType === "email" ? emailAddress : phoneNumber}
            </strong>
          </Text>
        </div>

        {/* OTP Form */}
        <Form
          form={form}
          onFinish={handleVerify}
          layout="vertical"
          style={{ marginBottom: "24px" }}
        >
          <div style={{ marginBottom: "32px" }}>
            <Text
              style={{
                display: "block",
                marginBottom: "12px",
                color: isDark ? "#ffffff" : "#000000",
                fontSize: "14px",
                fontWeight: 500,
              }}
            >
              Enter Verification Code
            </Text>
            <div style={{ display: "flex", gap: "12px", justifyContent: "space-between" }}>
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <Form.Item
                  key={index}
                  name={`otp${index}`}
                  rules={[{ required: true, message: "" }]}
                  style={{ margin: 0, flex: 1 }}
                >
                  <Input
                    maxLength={1}
                    onKeyUp={(e) => handleOtpKeyUp(index, e)}
                    style={{
                      width: "100%",
                      height: "56px",
                      fontSize: "24px",
                      textAlign: "center",
                      fontWeight: "600",
                      borderRadius: "12px",
                      borderColor: isDark ? "#434343" : "#d9d9d9",
                      backgroundColor: isDark ? "#262626" : "#ffffff",
                      color: isDark ? "#ffffff" : "#000000",
                    }}
                  />
                </Form.Item>
              ))}
            </div>
          </div>

          <Button
            type="primary"
            htmlType="submit"
            block
            loading={isLoading}
            style={{
              height: "48px",
              fontSize: "16px",
              fontWeight: "600",
              borderRadius: "8px",
              backgroundColor: tenant.theme.colorPrimary,
              borderColor: tenant.theme.colorPrimary,
            }}
          >
            Verify Account
          </Button>
        </Form>

        {/* Resend Code */}
        <div style={{ textAlign: "center", marginTop: "24px" }}>
          <Text style={{ color: isDark ? "#8c8c8c" : "#595959" }}>
            Didn't receive the code?{" "}
          </Text>
          {canResend ? (
            <Link
              onClick={handleResendCode}
              disabled={isResending}
              style={{
                color: tenant.theme.colorPrimary,
                fontWeight: "500",
                cursor: isResending ? "not-allowed" : "pointer",
              }}
            >
              {isResending ? "Sending..." : "Resend Code"}
            </Link>
          ) : (
            <Text style={{ color: isDark ? "#8c8c8c" : "#595959" }}>
              Resend in {countdown}s
            </Text>
          )}
        </div>

        {/* Back to Login */}
        <div style={{ textAlign: "center", marginTop: "32px" }}>
          <Link
            onClick={() => navigate("/authentication/login")}
            style={{
              color: isDark ? "#8c8c8c" : "#595959",
              fontSize: "14px",
            }}
          >
            ← Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default VerificationPage;
